/**
 * StickerCard — Figurinha individual do Álbum da Copa 2026.
 *
 * Molduras:
 *   - titular       → borda teal #7cffeb, fundo azul escuro
 *   - craque/tecnico → borda dourada #fbd34d, efeito jjSheen
 *   - lendaria_mascote → borda rosé #dca09b
 *   - lendaria_estadio → borda azul #5b8ad6
 *   - lendaria_taca    → borda dourada + glow pulsante
 *
 * Estados: conquistada | repetida | bloqueada | celebracao
 *
 * Chip de posição (top-left):
 *   - titular  → GOL / LAT / MEI / ATA derivado do behavior
 *   - craque   → ★ (nunca um chip de campo — FIX: impede "LAT + CRAQUE")
 *   - tecnico  → TEC
 *   - lendaria → sem chip
 */

import React from 'react';
import { Sticker, StickerType } from 'services/copa.service';
import { ColleagueFace } from '../ColleagueFace';
import styles from './StickerCard.module.scss';

// ---------------------------------------------------------------------------
// Tipos adicionais de moldura e estado
// ---------------------------------------------------------------------------
export type StickerVariant = 'titular' | 'craque' | 'lendaria_mascote' | 'lendaria_estadio' | 'lendaria_taca';

export type StickerState = 'conquistada' | 'repetida' | 'bloqueada' | 'celebracao';

export type PositionChip = 'GOL' | 'LAT' | 'MEI' | 'ATA' | 'TEC' | '★';

// ---------------------------------------------------------------------------
// Mapeamentos
// ---------------------------------------------------------------------------
const TYPE_LABEL: Record<StickerType, string> = {
  titular: 'Titular',
  craque: 'Craque',
  tecnico: 'Técnico',
  lendaria: 'Lendária',
};

const TYPE_RARITY_LABEL: Record<StickerType, string> = {
  titular: 'TITULAR',
  craque: 'CRAQUE',
  tecnico: 'TÉCNICO',
  lendaria: 'LENDÁRIA',
};

/** Derivar moldura a partir do tipo quando o caller não informa explicitamente */
function variantFromType(type: StickerType): StickerVariant {
  if (type === 'craque' || type === 'tecnico') return 'craque';
  if (type === 'lendaria') return 'lendaria_taca'; // default lendária
  return 'titular';
}

/** Chip de campo — apenas para Titulares (derivado do behavior) */
const BEHAVIOR_TO_CHIP: Record<string, PositionChip> = {
  foco: 'GOL',
  colaboracao: 'LAT',
  inovacao: 'MEI',
  resolutividade: 'ATA',
};

/** Rótulo de posição legível (linha 1 do footer) */
const BEHAVIOR_TO_POSITION_LABEL: Record<string, string> = {
  foco: 'Goleiro',
  colaboracao: 'Lateral',
  inovacao: 'Meio-campo',
  resolutividade: 'Atacante',
  lideranca: 'Técnico',
};

/** Nome de exibição do comportamento (linha 2 do footer) */
const BEHAVIOR_TO_DISPLAY_NAME: Record<string, string> = {
  foco: 'Foco no cliente',
  colaboracao: 'Colaboração',
  inovacao: 'Inovação',
  resolutividade: 'Protagonismo',
  lideranca: 'Liderança Integradora',
};

/**
 * Chip de posição correto por tipo de carta.
 * FIX: craques não recebem chip de campo (GOL/LAT/MEI/ATA) — usam ★.
 */
function chipFromType(type: StickerType, behavior: string): PositionChip | undefined {
  if (type === 'titular') {
    const key = behavior?.toLowerCase();
    return BEHAVIOR_TO_CHIP[key];
  }
  if (type === 'craque') return '★';
  if (type === 'tecnico') return 'TEC';
  // lendaria → sem chip
  return undefined;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface StickerCardProps {
  sticker: Sticker;
  /** Moldura visual (derivada de sticker.type se não informada) */
  variant?: StickerVariant;
  /** Estado de exibição da carta */
  cardState?: StickerState;
  /** Número de cópias repetidas (aparece no badge de repetida) */
  repeatCount?: number;
  /** Mostra sticker em modo "vazio" (posição ainda não preenchida) */
  isEmpty?: boolean;
  /** Posição no álbum (ex: 'goleiro'), para label de acessibilidade */
  position?: string;
  /** Slot vazio que o jogador JÁ pode preencher (tem figurinha elegível na Coleção) */
  hasCollatable?: boolean;
  /** Clique na carta (ex: selecionar para colar) */
  onClick?: () => void;
  /** Estilo visual compacto (na Coleção) */
  compact?: boolean;
  /** Desabilitado quando colleagueLocked ou collected */
  disabled?: boolean;
  /** Realça a carta (selecionada na coleção) */
  selected?: boolean;
  /** Omite o nome interno — usado quando o caller renderiza seu próprio nome
   *  (ex.: link do Humanograma), que não pode ficar aninhado dentro do <button> da carta */
  hideName?: boolean;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------
export function StickerCard({
  sticker,
  variant,
  cardState = 'conquistada',
  repeatCount,
  isEmpty = false,
  position,
  hasCollatable = false,
  onClick,
  compact = false,
  disabled = false,
  selected = false,
  hideName = false,
}: StickerCardProps) {
  const resolvedVariant = variant ?? variantFromType(sticker.type);
  // FIX: chip derivado do tipo, não só do behavior
  const positionChip = chipFromType(sticker.type, sticker.behavior);
  const rarityLabel = TYPE_RARITY_LABEL[sticker.type] ?? sticker.type.toUpperCase();
  const label = `${TYPE_LABEL[sticker.type] ?? sticker.type}: ${sticker.colleagueName ?? sticker.colleagueId}`;

  // Rótulo de posição e nome de exibição do comportamento
  const behaviorKey = sticker.behavior?.toLowerCase();
  const positionLabel = BEHAVIOR_TO_POSITION_LABEL[behaviorKey] ?? '';
  const behaviorDisplayName = BEHAVIOR_TO_DISPLAY_NAME[behaviorKey] ?? sticker.behavior ?? '';

  // Inicial para fallback do avatar (último recurso). charAt evita crash com string vazia
  // (slots vazios têm colleagueId='') — `||` cobre '' que o `??` deixaria passar.
  const initial = (sticker.colleagueName || sticker.colleagueId || '?').charAt(0).toUpperCase();
  const faceSize = compact ? 40 : 66;

  // Map variant to SCSS class name
  const VARIANT_CLASS: Record<StickerVariant, string> = {
    titular: styles.variant_titular,
    craque: styles.variant_craque,
    lendaria_mascote: styles.variant_lendaria_mascote,
    lendaria_estadio: styles.variant_lendaria_estadio,
    lendaria_taca: styles.variant_lendaria_taca,
  };

  const STATE_CLASS: Record<StickerState, string> = {
    conquistada: '',
    repetida: styles.state_repetida,
    bloqueada: styles.state_bloqueada,
    celebracao: styles.state_celebracao,
  };

  const variantClass = VARIANT_CLASS[resolvedVariant] ?? '';
  const stateClass = STATE_CLASS[cardState] ?? '';

  const cardClasses = [
    styles.card,
    variantClass,
    stateClass,
    compact ? styles.compact : '',
    selected ? styles.selected : '',
    disabled ? styles.disabled : '',
    onClick && !disabled ? styles.clickable : '',
  ]
    .filter(Boolean)
    .join(' ');

  // ---------------------------------------------------------------------------
  // Slot vazio (posição não preenchida — A CONQUISTAR)
  // ---------------------------------------------------------------------------
  if (isEmpty) {
    const emptyClasses = [
      styles.card,
      variantClass,
      styles.state_bloqueada,
      hasCollatable ? styles.collatableSlot : '',
      onClick && !disabled ? styles.clickable : '',
    ]
      .filter(Boolean)
      .join(' ');

    // Rótulo legível para o leitor de tela: Titular usa a posição derivada do
    // behavior (Goleiro/Zagueiro/...); Craque/Técnico usam o nome do tipo —
    // nunca a string crua de `position` (ex.: "defensor1").
    const emptyPositionLabel =
      sticker.type === 'titular'
        ? positionLabel || position || ''
        : TYPE_LABEL[sticker.type] ?? position ?? '';

    return (
      <button
        className={emptyClasses}
        onClick={!disabled ? onClick : undefined}
        aria-label={
          hasCollatable
            ? `Posição ${emptyPositionLabel} — você tem uma figurinha para colar aqui`
            : `Posição vazia: ${emptyPositionLabel}`
        }
        aria-disabled={disabled}
        type="button"
      >
        {/* Chip de posição (topo-esquerda) */}
        {positionChip && (
          <span className={styles.positionChip} aria-hidden="true">
            {positionChip}
          </span>
        )}
        {/* Badge: "VOCÊ TEM!" quando há figurinha elegível; senão "A CONQUISTAR" */}
        <span className={`${styles.rarityBadge} ${hasCollatable ? styles.rarityBadgeCollatable : ''}`} aria-hidden="true">
          {hasCollatable ? 'VOCÊ TEM!' : 'A CONQUISTAR'}
        </span>
        {/* Avatar silhueta */}
        <div className={styles.avatarWrapper} aria-hidden="true">
          <div className={`${styles.avatarCircle} ${styles.avatarLocked}`}>
            <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" className={styles.silhouette}>
              <path d="M12 14c2.7 0 8 1.3 8 4v2H4v-2c0-2.7 5.3-4 8-4zm0-2a4 4 0 1 1 0-8 4 4 0 0 1 0 8z" />
            </svg>
          </div>
        </div>
        <p className={styles.name} aria-hidden="true">
          ???
        </p>
        <div className={styles.footer} aria-hidden="true">
          <div className={styles.footerPosition}>{positionLabel}</div>
          <div className={styles.footerBehavior}>{behaviorDisplayName}</div>
        </div>
      </button>
    );
  }

  // ---------------------------------------------------------------------------
  // Carta bloqueada
  // ---------------------------------------------------------------------------
  if (cardState === 'bloqueada') {
    return (
      <div className={cardClasses} role="img" aria-label={`Posição bloqueada: ${position ?? ''}`}>
        {positionChip && (
          <span className={styles.positionChip} aria-hidden="true">
            {positionChip}
          </span>
        )}
        <span className={styles.rarityBadge} aria-hidden="true">
          {rarityLabel}
        </span>
        <div className={styles.avatarWrapper} aria-hidden="true">
          <div className={`${styles.avatarCircle} ${styles.avatarLocked}`}>
            <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" className={styles.silhouette}>
              <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5zm3 8H9V6a3 3 0 0 1 6 0v3z" />
            </svg>
          </div>
        </div>
        <p className={styles.name} aria-hidden="true">
          ???
        </p>
        <div className={styles.footer} aria-hidden="true">
          <div className={styles.footerPosition}>{positionLabel}</div>
          <div className={styles.footerBehavior}>{behaviorDisplayName}</div>
        </div>
      </div>
    );
  }

  const displayName = sticker.colleagueName ? sticker.colleagueName : sticker.colleagueId.slice(0, 8);

  // Carta colada e clicável → abre o reconhecimento por trás (RN-30).
  // Mostra uma indicação visual ("Ver reconhecimento") para sinalizar o clique.
  const showReadHint = !!onClick && !compact && cardState !== 'celebracao';

  const cardContent = (
    <>
      {/* Chip de posição (top-left) */}
      {positionChip && (
        <span className={styles.positionChip} aria-hidden="true">
          {positionChip}
        </span>
      )}

      {/* Badge de raridade (top-right) */}
      <span className={styles.rarityBadge} aria-hidden="true">
        {rarityLabel}
      </span>

      {/* Face do colega: foto → avatar → inicial */}
      <div className={styles.avatarWrapper} aria-hidden="true">
        <div className={styles.avatarCircle}>
          <ColleagueFace
            image={sticker.image}
            avatar={sticker.avatar}
            avatarType={sticker.avatarType}
            initial={initial}
            size={faceSize}
            initialClassName={styles.avatarInitial}
          />
        </div>
      </div>

      {/* Nome do colega — hideName troca o texto por um espaçador do mesmo
          bloco: o caller (Album) desenha seu próprio nome+matrícula por cima
          (link do Humanograma não pode aninhar dentro do <button> da carta),
          mas o footer precisa continuar ocupando a mesma posição de sempre. */}
      {!compact && (hideName ? (
        <div className={styles.nameSpacer} aria-hidden="true" />
      ) : (
        <p className={styles.name} aria-hidden="true">
          {displayName}
        </p>
      ))}

      {/* Footer: duas linhas — rótulo posição + comportamento */}
      {!compact && (
        <div className={styles.footer} aria-hidden="true">
          {cardState === 'repetida' ? (
            <span className={styles.footerRepetida}>Repetida {repeatCount && repeatCount > 1 ? `×${repeatCount}` : ''}</span>
          ) : (
            <>
              <div className={styles.footerPosition}>{positionLabel}</div>
              <div className={styles.footerBehaviorHighlight}>{behaviorDisplayName}</div>
            </>
          )}
        </div>
      )}

      {/* Indicação de leitura do reconhecimento (carta colada e clicável).
          Ícone persistente no canto + faixa "Ver reconhecimento" que surge no hover/foco. */}
      {showReadHint && (
        <>
          <span className={styles.readHintIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0A4.5 4.5 0 1 1 14 9.5 4.49 4.49 0 0 1 9.5 14z" />
            </svg>
          </span>
          <span className={styles.readHint} aria-hidden="true">
            Ver reconhecimento
          </span>
        </>
      )}

      {/* Overlay de celebração */}
      {cardState === 'celebracao' && <div className={styles.celebracaoOverlay} aria-hidden="true" />}
    </>
  );

  // Sem onClick → carta puramente decorativa (picker de "adicionar carta", reveal
  // do pacote, prateleira lendária, slot somente-leitura). Renderiza como
  // role="img" — igual ao ramo bloqueada — para não inserir um <button> inerte e
  // focável na ordem de tabulação (o botão real é o "+ Adicionar" ao lado).
  if (!onClick) {
    return (
      <div className={cardClasses} role="img" aria-label={label}>
        {cardContent}
      </div>
    );
  }

  return (
    <button
      className={cardClasses}
      onClick={!disabled ? onClick : undefined}
      aria-label={showReadHint ? `${label}. Ative para ver o reconhecimento.` : label}
      aria-pressed={selected}
      aria-disabled={disabled}
      type="button"
    >
      {cardContent}
    </button>
  );
}
